import React, { useEffect, useState } from 'react';
import { Transition } from '@headlessui/react';
import cx from 'classnames';

import { Close } from '../Icons';
import Portal from '../HOC/Portal';

type ModalContainerProps = {
  isOpen: boolean;
  onClose?: () => void;
  isCloseOnOutsideClick?: boolean;
  mobileSideDirection?: 'top' | 'bottom' | 'left' | 'right';
  children: React.ReactNode;
  className?: string;
  panelClassName?: string;
};

const ModalContainer = (props: ModalContainerProps) => {
  const {
    isCloseOnOutsideClick,
    isOpen,
    onClose = () => {},
    children,
    className,
    panelClassName,
    mobileSideDirection,
    ...extend
  } = props;

  const [open, setOpen] = useState<boolean>(isOpen);

  useEffect(() => {
    setOpen(isOpen);
    return () => setOpen(false);
  }, [isOpen]);

  return (
    <Portal>
      <Transition appear show={open} as={React.Fragment}>
        <div className={cx('fixed inset-0 z-[999]', className && className)}>
          <Transition.Child
            enter="transition-opacity ease-linear duration-500"
            enterFrom="opacity-0"
            enterTo="opacity-75"
            leave="transition-opacity ease-linear duration-500"
            leaveFrom="opacity-75"
            leaveTo="opacity-0"
            as={React.Fragment}
          >
            <div
              className="fixed inset-0 bg-[#E3E3E3] bg-opacity-75 w-full h-full"
              onClick={() => {
                if (isCloseOnOutsideClick) {
                  setOpen(false);
                  onClose && onClose();
                }
              }}
            />
          </Transition.Child>
          <div className="modal-wrapper">
            <div className="modal-panel-container">
              <Transition.Child
                as={React.Fragment}
                enter="ease-in-out duration-500 md:duration-200 transform transition"
                enterFrom={cx(
                  'opacity-100 md:opacity-0 md:scale-95',
                  mobileSideDirection === 'top'
                    ? 'max-md:-translate-y-full'
                    : mobileSideDirection === 'bottom'
                    ? 'max-md:translate-y-full'
                    : mobileSideDirection === 'left'
                    ? 'max-md:-translate-x-full'
                    : mobileSideDirection === 'right'
                    ? 'max-md:translate-x-full'
                    : ''
                )}
                enterTo={cx(
                  'md:opacity-100 md:scale-100',
                  mobileSideDirection === 'top' ||
                    mobileSideDirection === 'bottom'
                    ? 'max-md:translate-y-0'
                    : mobileSideDirection === 'left' ||
                      mobileSideDirection === 'right'
                    ? 'max-md:translate-x-0'
                    : ''
                )}
                leave={cx(
                  'ease-out transform transition duration-500 md:duration-200',
                  { 'delay-100 md:delay-0': mobileSideDirection }
                )}
                // leaveFrom={cx('opacity-100 md:scale-100')}
                leaveTo={cx(
                  'opacity-0 md:scale-95',
                  mobileSideDirection === 'top'
                    ? 'max-md:-translate-y-full'
                    : mobileSideDirection === 'bottom'
                    ? 'max-md:translate-y-full'
                    : mobileSideDirection === 'left'
                    ? 'max-md:-translate-x-full'
                    : mobileSideDirection === 'right'
                    ? 'max-md:translate-x-full'
                    : ''
                )}
              >
                <div
                  className={cx(
                    'modal-panel bg-white text-left',
                    mobileSideDirection ? mobileSideDirection : 'm-4 md:m-0',
                    panelClassName && panelClassName
                  )}
                >
                  {React.Children.map<React.ReactNode, React.ReactNode>(
                    children,
                    (child) => {
                      if (React.isValidElement(child)) {
                        return React.cloneElement(child, extend);
                      }
                    }
                  )}
                </div>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Transition>
    </Portal>
  );
};

type ModalHeaderProps = {
  children: React.ReactElement | React.ReactNode;
  onClose?: () => void;
  className?: string;
};

const ModalHeader = (props: ModalHeaderProps) => {
  const { className, children, onClose } = props;
  return (
    <div
      className={cx(
        'text-[18px] font-bold leading-6 bg-white text-gray-900 pl-[15px] pr-[30px] py-2.5 relative h-11 border-b border-primary',
        className && className
      )}
    >
      {children}
      {onClose && (
        <button
          className="absolute right-2 top-1/2 -translate-y-1/2"
          onClick={onClose}
        >
          <Close width={22} />
        </button>
      )}
    </div>
  );
};

type ModalBodyProps = {
  children: React.ReactElement | React.ReactNode;
  className?: string;
};

const ModalBody = (props: ModalBodyProps) => {
  const { children, className } = props;
  return (
    <div className={cx('bg-secondary p-[15px] flex-1', className && className)}>
      {children}
    </div>
  );
};

type ModalFooterProps = {
  className?: string;
  children: React.ReactElement | React.ReactNode;
};

const ModalFooter = (props: ModalFooterProps) => {
  const { className, children } = props;
  return (
    <div className={cx('bg-secondary p-[15px]', className && className)}>
      {children}
    </div>
  );
};

interface CompoundedComponent
  extends React.ForwardRefExoticComponent<ModalContainerProps> {
  Header: typeof ModalHeader;
  Body: typeof ModalBody;
  Footer: typeof ModalFooter;
}

const Modal = ModalContainer as CompoundedComponent;
Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;

export default Modal;
